import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useData } from '../../../contexts/DataContext';
import InternalChat from '../../../components/InternalChat';
import UserGuide from '../../../components/UserGuide';
import ReferralBanner from '../../../components/ReferralBanner';
import BookingOverview from '../../superadmin/pages/BookingOverview';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { FiBriefcase, FiDollarSign, FiUsers, FiHome, FiMessageSquare, FiBook, FiLogOut, FiTrendingUp, FiCalendar } = FiIcons;

const PartnerDashboard = () => {
  const { user, logout } = useAuth();
  const { getSalesByPartner, getUnreadCount } = useData();
  const [sales, setSales] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const navigate = useNavigate();

  useEffect(() => {
    const effectiveId = user?.partnerId || user?.partner_id || user?.id;
    if (effectiveId && getSalesByPartner) {
      setSales(getSalesByPartner(effectiveId) || []);
    }
  }, [user, getSalesByPartner]);

  // Handle logout with redirect
  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const unreadMessages = getUnreadCount(user?.id);
  const totalRevenue = sales.reduce((sum, sale) => sum + (sale.monthlyFee || 0), 0);
  const estimatedCommission = totalRevenue * 0.15;

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center shadow-lg shadow-indigo-100">
                <SafeIcon icon={FiBriefcase} className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-lg font-black text-gray-900 uppercase tracking-tight">Partner Dashboard</h1>
                <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">{user?.name}</p>
              </div>
            </div>
            <button 
              onClick={handleLogout} 
              className="px-5 py-2.5 bg-gray-50 text-red-600 border border-red-50 rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-red-50 transition-all flex items-center gap-2"
            >
              <SafeIcon icon={FiLogOut} /> Logg ut
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <ReferralBanner id={user?.partnerId || user?.partner_id} type="partner" name={user?.name} />

        <div className="bg-white p-2 rounded-2xl shadow-sm border border-gray-100 flex gap-2 mb-10 overflow-x-auto no-scrollbar">
          {[
            { id: 'overview', label: 'Oversikt & Salg', icon: FiTrendingUp },
            { id: 'calendar', label: 'Booking & Kalender', icon: FiCalendar },
            { id: 'resources', label: 'Ressurssenter', icon: FiBook },
            { id: 'chat', label: 'Meldinger', icon: FiMessageSquare, badge: unreadMessages }
          ].map(tab => (
            <button 
              key={tab.id} 
              onClick={() => setActiveTab(tab.id)} 
              className={`flex items-center gap-3 px-6 py-3 rounded-xl text-sm font-black uppercase tracking-wider transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-xl' : 'text-gray-500 hover:bg-gray-50 hover:text-indigo-600'}`}
            >
              <SafeIcon icon={tab.icon} /> {tab.label} {tab.badge > 0 && <span className="bg-red-500 text-white text-[10px] px-2 py-0.5 rounded-full ml-1">{tab.badge}</span>}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div 
              key="overview" 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: -20 }} 
              className="space-y-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white p-8 rounded-[32px] shadow-sm border border-gray-100">
                  <div className="flex items-center gap-5">
                    <div className="p-4 bg-green-50 rounded-2xl text-green-600"><SafeIcon icon={FiUsers} className="text-2xl" /></div>
                    <div><p className="text-xs font-black text-gray-400 uppercase tracking-widest">Kunder</p><p className="text-4xl font-black text-gray-900">{sales.length}</p></div>
                  </div>
                </div>
                <div className="bg-white p-8 rounded-[32px] shadow-sm border border-gray-100">
                  <div className="flex items-center gap-5">
                    <div className="p-4 bg-purple-50 rounded-2xl text-purple-600"><SafeIcon icon={FiDollarSign} className="text-2xl" /></div>
                    <div><p className="text-xs font-black text-gray-400 uppercase tracking-widest">Dagens Provisjon</p><p className="text-4xl font-black text-gray-900">{estimatedCommission.toLocaleString()} kr</p></div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-[32px] shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-8 py-6 border-b border-gray-50 bg-gray-50/50">
                  <h3 className="font-black text-gray-900 uppercase tracking-tight">Kundeportefølje</h3>
                </div>
                <table className="w-full text-left">
                  <thead className="bg-gray-50/30 text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    <tr><th className="px-8 py-5">Bedrift</th><th className="px-8 py-5">Pakke</th><th className="px-8 py-5">Status</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {sales.map(sale => (
                      <tr key={sale.id} className="hover:bg-blue-50/20 transition-colors">
                        <td className="px-8 py-6 font-bold">{sale.name}</td>
                        <td className="px-8 py-6 text-sm capitalize">{sale.subscription}</td>
                        <td className="px-8 py-6">
                          <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase ${sale.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{sale.active ? 'Aktiv' : 'Inaktiv'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {activeTab === 'calendar' && (
            <motion.div key="calendar" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <BookingOverview forceAgentId={user?.partnerId || user?.id} role="partner" />
            </motion.div>
          )}

          {activeTab === 'resources' && (
            <motion.div key="resources" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <UserGuide userType="partner" isEmbedded={true} />
            </motion.div>
          )}

          {activeTab === 'chat' && (
            <motion.div key="chat" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }}>
              <InternalChat currentUserType="partner" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default PartnerDashboard;